import React, { useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { DateTime } from 'luxon';
import { cloneDeep } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Schedule } from '../../interfaces/Schedule';
import { Shift } from '../../interfaces/Shift';
import { PtoSubtype } from '../../interfaces/PtoSubtype';
import {
  occupiedShifts,
  reserveShiftOptions,
  shiftOptions,
} from '../../variables/Schedule';
import {
  compareSchedulePersonnelNames,
  getReserveWorkedHours,
  hoursFromShift,
  hoursRangeStrFromShift,
  hoursStringFromShift,
} from '../../utils/Schedule';
import { isHoliday, isWorkday } from '../../utils/Date';
import {
  getMinimumPersonnelCountsByDay,
  getWorkingPersonnelCountsByDay,
} from '../../utils/PersonnelCounts';

type View = 'grid' | 'day' | 'person';
interface Props {
  reserve?: boolean;
  schedule: Schedule;
  dates: Date[];
  readOnly: boolean;
  statsLoading: boolean;
  onChange: (schedule: Schedule) => void;
}

export default function MobileSchedulePlan({
  reserve = false,
  schedule,
  dates,
  readOnly,
  statsLoading,
  onChange,
}: Props) {
  const { t } = useTranslation();
  const assignments = reserve ? schedule.reserveList : schedule.plan;
  const availableShifts = reserve ? reserveShiftOptions : shiftOptions;
  const [managingPersonnel, setManagingPersonnel] = useState(false);
  const [view, setView] = useState<View>('grid');
  const dateKeys = dates.map((date) => DateTime.fromJSDate(date).toISODate()!);
  const today = DateTime.local().toISODate()!;
  const [date, setDate] = useState(
    dateKeys.includes(today) ? today : dateKeys[0] ?? '',
  );
  const names = useMemo(
    () =>
      Object.keys(assignments).sort((a, b) =>
        compareSchedulePersonnelNames(a, b, schedule),
      ),
    [assignments, schedule],
  );
  const [selectedPerson, setPerson] = useState(names[0] ?? '');
  const person = names.includes(selectedPerson)
    ? selectedPerson
    : names[0] ?? '';
  const availablePersonnel = Object.keys(schedule.plan).filter(
    (name) => !names.includes(name),
  );
  const changePersonnel = (oldName: string, newName: string) => {
    if (
      readOnly ||
      !reserve ||
      (newName &&
        (names.includes(newName) ||
          !Object.hasOwnProperty.call(schedule.plan, newName)))
    )
      return;
    const next = cloneDeep(schedule);
    if (newName) next.reserveList[newName] = next.reserveList[oldName] ?? {};
    if (oldName) delete next.reserveList[oldName];
    if (person === oldName || !person) setPerson(newName);
    onChange(next);
  };
  const [editing, setEditing] = useState<{ name: string; date: string } | null>(
    null,
  );
  const [value, setValue] = useState('');
  const [overtime, setOvertime] = useState('0');
  const grid = useRef<HTMLDivElement>(null);
  const minimum = getMinimumPersonnelCountsByDay(schedule);
  const working = getWorkingPersonnelCountsByDay(schedule);
  const valueFor = (name: string, day: string): string => {
    const shift = assignments[name]?.[day] ?? Shift.EMPTY;
    return shift === Shift.PTO || shift === Shift.ETO
      ? schedule.ptoSubtypes[name]?.[day] ??
          (shift === Shift.PTO ? PtoSubtype.ANNUAL : PtoSubtype.SPECIAL_REQUEST)
      : shift;
  };
  const label = (name: string, day: string, full = false) => {
    const assignment = valueFor(name, day);
    if (!shiftOptions.includes(assignment as Shift))
      return t(`pto_subtype.${full ? 'name' : 'abbreviation'}.${assignment}`);
    if (!assignment) return '—';
    const hours = hoursStringFromShift(
      schedule,
      schedule.fourDPersonnelNames.includes(name),
      name,
      reserve ? undefined : day,
      assignment as Shift,
    );
    return full ? `${t(`schedule.shifts.${assignment}`)} · ${hours}` : hours;
  };
  const warnings = (day: string) => {
    if (reserve) return [];
    const actual = working[day] ?? { day: 0, night: 0 };
    const expected = minimum[day] ?? { day: 0, night: 0 };
    const weekday = isWorkday(DateTime.fromISO(day).toJSDate());
    return (['day', 'night'] as const)
      .filter((shift) =>
        shift === 'night'
          ? actual.night !== expected.night
          : actual.day < expected.day ||
            actual.day > expected.day + (weekday ? 1 : 0),
      )
      .map((shift) =>
        t('schedule.warning.personnel_count', {
          shift: t(`schedule.shifts.${shift.toUpperCase()}`),
          actual: actual[shift],
          expected: expected[shift] + (shift === 'day' && weekday ? 1 : 0),
        }),
      );
  };
  const restWarning = (name: string, day: string) => {
    if (reserve)
      return assignments[name]?.[day] && schedule.plan[name]?.[day]
        ? t('schedule.warning.reserve_conflict')
        : '';
    const shift = schedule.plan[name]?.[day];
    const next = DateTime.fromISO(day).plus({ days: 1 }).toISODate()!;
    return (shift === Shift.NIGHT || shift === Shift.ALL_DAY) &&
      occupiedShifts.includes(schedule.plan[name]?.[next])
      ? t(
          shift === Shift.NIGHT
            ? 'schedule.warning.night'
            : 'schedule.warning.all_day',
        )
      : '';
  };
  const openCell = (name: string, day: string) => {
    setPerson(name);
    setDate(day);
    setEditing({ name, date: day });
    setValue(valueFor(name, day));
    setOvertime(String(schedule.overtimeHours[name]?.[day] ?? 0));
  };
  const selectedShift = shiftOptions.includes(value as Shift)
    ? (value as Shift)
    : value === PtoSubtype.SPECIAL_REQUEST
    ? Shift.ETO
    : Shift.PTO;
  const baseHours = editing
    ? hoursFromShift(schedule, editing.name, selectedShift)
    : 0;
  const validOvertime =
    overtime.trim() !== '' &&
    Number.isFinite(Number(overtime)) &&
    Number(overtime) * 2 === Math.round(Number(overtime) * 2) &&
    baseHours + Number(overtime) >= 0;
  const apply = () => {
    if (
      !editing ||
      readOnly ||
      (!reserve && !validOvertime) ||
      (!availableShifts.includes(value as Shift) && reserve)
    )
      return;
    const next = cloneDeep(schedule);
    const { name, date: day } = editing;
    if (reserve) {
      if (value === Shift.EMPTY) delete next.reserveList[name][day];
      else next.reserveList[name][day] = value as Shift;
      onChange(next);
      setEditing(null);
      return;
    }
    next.ptoSubtypes[name] ??= {};
    delete next.ptoSubtypes[name][day];
    if (value === Shift.EMPTY) delete next.plan[name][day];
    else {
      next.plan[name][day] = selectedShift;
      if (!shiftOptions.includes(value as Shift))
        next.ptoSubtypes[name][day] = value as PtoSubtype;
    }
    next.overtimeHours[name] ??= {};
    next.overtimeHours[name][day] =
      selectedShift &&
      selectedShift !== Shift.PTO &&
      selectedShift !== Shift.ETO
        ? Number(overtime)
        : 0;
    onChange(next);
    setEditing(null);
  };
  const selectDate = (next: string) => {
    setDate(next);
    const cell = grid.current?.querySelector<HTMLElement>(
      `[data-date="${next}"]`,
    );
    if (cell && grid.current) grid.current.scrollLeft = cell.offsetLeft - 130;
  };
  const dateLabel = (day: string) =>
    DateTime.fromISO(day).toLocaleString({
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
  const row = (name: string, day: string, title: string) => (
    <ListItemButton
      key={`${name}-${day}`}
      onClick={() => openCell(name, day)}
      sx={{ minHeight: 56, borderBottom: 1, borderColor: 'divider' }}
    >
      <ListItemText
        primary={title}
        secondary={`${label(name, day, true)}${
          !reserve && schedule.overtimeHours[name]?.[day]
            ? ` · ${t('schedule.overtime_hours')}: ${
                schedule.overtimeHours[name][day]
              }`
            : ''
        }${restWarning(name, day) ? ' ⚠' : ''}`}
      />
    </ListItemButton>
  );
  return (
    <Stack spacing={1.5} sx={{ minWidth: 0, py: 1.5 }}>
      <ToggleButtonGroup
        exclusive
        fullWidth
        size='small'
        value={view}
        onChange={(_, nextView) => {
          if (nextView) setView(nextView);
        }}
        aria-label={String(t('schedule.mobile.view'))}
      >
        {(['grid', 'day', 'person'] as const).map((option) => (
          <ToggleButton key={option} value={option}>
            {t(`schedule.mobile.${option}`)}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      {reserve && !readOnly && (
        <Button variant='outlined' onClick={() => setManagingPersonnel(true)}>
          {t('schedule.mobile.manageReserve')}
        </Button>
      )}
      {view === 'day' && (
        <>
          <Stack direction='row' spacing={1}>
            <TextField
              select
              fullWidth
              size='small'
              label={t('schedule.mobile.date')}
              value={date}
              onChange={(event) => selectDate(event.target.value)}
            >
              {dateKeys.map((day) => (
                <MenuItem key={day} value={day}>
                  {dateLabel(day)}
                </MenuItem>
              ))}
            </TextField>
            <Button
              disabled={!dateKeys.includes(today)}
              onClick={() => selectDate(today)}
            >
              {t('schedule.mobile.today')}
            </Button>
          </Stack>
          {warnings(date).map((warning) => (
            <Alert key={warning} severity='warning'>
              {warning}
            </Alert>
          ))}
        </>
      )}
      {view === 'grid' && (
        <TableContainer
          ref={grid}
          sx={{
            maxHeight: '60dvh',
            overflow: 'auto',
            position: 'relative',
            border: 1,
            borderColor: 'divider',
          }}
          tabIndex={0}
          role='region'
          aria-label={t('schedule.mobile.grid')}
        >
          <Table
            stickyHeader
            size='small'
            sx={{
              width: 'max-content',
              '& th, & td': { p: 0, borderRight: 1, borderColor: 'divider' },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{ left: 0, zIndex: 4, minWidth: 130, maxWidth: 130 }}
                >
                  <Typography
                    component='span'
                    variant='inherit'
                    sx={{ display: 'block', px: 1 }}
                  >
                    {t('schedule.mobile.person')}
                  </Typography>
                </TableCell>
                {dateKeys.map((day, index) => (
                  <TableCell
                    key={day}
                    data-date={day}
                    sx={{
                      minWidth: 76,
                      bgcolor:
                        !isWorkday(dates[index]) ||
                        isHoliday(dates[index], schedule.fullDayDates)
                          ? 'action.selected'
                          : 'background.paper',
                    }}
                  >
                    <Button
                      size='small'
                      onClick={() => {
                        setDate(day);
                        setView('day');
                      }}
                      sx={{ width: '100%', minHeight: 48 }}
                    >
                      {dateLabel(day)}
                      {warnings(day).length > 0 ? ' ⚠' : ''}
                    </Button>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {names.map((name) => (
                <TableRow key={name}>
                  <TableCell
                    component='th'
                    scope='row'
                    sx={{
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      bgcolor: 'background.paper',
                      width: 130,
                      maxWidth: 130,
                    }}
                  >
                    <Button
                      onClick={() => {
                        setPerson(name);
                        setView('person');
                      }}
                      sx={{
                        textAlign: 'left',
                        justifyContent: 'flex-start',
                        width: '100%',
                        minHeight: 48,
                      }}
                    >
                      {name}
                    </Button>
                  </TableCell>
                  {dateKeys.map((day, index) => (
                    <TableCell
                      key={day}
                      sx={{
                        bgcolor:
                          day === date
                            ? 'action.hover'
                            : !isWorkday(dates[index]) ||
                              isHoliday(dates[index], schedule.fullDayDates)
                            ? 'action.selected'
                            : undefined,
                      }}
                    >
                      <Button
                        color={restWarning(name, day) ? 'warning' : 'inherit'}
                        aria-label={`${name}, ${dateLabel(day)}, ${label(
                          name,
                          day,
                          true,
                        )}`}
                        onClick={() => openCell(name, day)}
                        sx={{
                          minHeight: 48,
                          width: '100%',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {label(name, day)}
                        {restWarning(name, day) ? ' ⚠' : ''}
                        {!reserve && schedule.overtimeHours[name]?.[day]
                          ? '*'
                          : ''}
                      </Button>
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {view === 'day' && (
        <List disablePadding>{names.map((name) => row(name, date, name))}</List>
      )}
      {view === 'person' && (
        <>
          <TextField
            select
            fullWidth
            size='small'
            label={t('schedule.mobile.person')}
            value={person}
            onChange={(event) => setPerson(event.target.value)}
          >
            {names.map((name) => (
              <MenuItem key={name} value={name}>
                {name}
              </MenuItem>
            ))}
          </TextField>
          <Typography aria-live='polite'>
            {reserve
              ? `${t('schedule.total_hours')}: ${getReserveWorkedHours(
                  schedule,
                  person,
                  assignments[person] ?? {},
                  dates,
                )}`
              : statsLoading
              ? t('schedule.mobile.calculating')
              : `${t('schedule.total_hours')}: ${
                  schedule.personnelStats[person]?.totalWorkedHours ?? 0
                } · ${t('schedule.total_hours_should_worked')}: ${
                  schedule.personnelStats[person]?.totalHoursShouldWorked ?? 0
                } · ${t('schedule.overtime_hours')}: ${
                  schedule.personnelStats[person]?.totalOvertimeHours ?? 0
                }`}
          </Typography>
          <List disablePadding>
            {person && dateKeys.map((day) => row(person, day, dateLabel(day)))}
          </List>
        </>
      )}
      {!names.length && (
        <Alert severity='info'>{t('schedule.mobile.noPersonnel')}</Alert>
      )}
      <Drawer
        anchor='bottom'
        open={!!editing}
        onClose={() => setEditing(null)}
        PaperProps={{
          sx: {
            borderRadius: '16px 16px 0 0',
            maxHeight: '90dvh',
            pb: 'var(--app-safe-area-bottom)',
          },
        }}
      >
        <Stack spacing={2} sx={{ p: 2 }}>
          <Typography variant='h6'>
            {editing?.name} · {editing && dateLabel(editing.date)}
          </Typography>
          {readOnly && (
            <Alert severity='info'>{t('schedule.mobile.readOnly')}</Alert>
          )}
          {editing && restWarning(editing.name, editing.date) && (
            <Alert severity='warning'>
              {restWarning(editing.name, editing.date)}
            </Alert>
          )}
          <TextField
            select
            label={t('schedule.mobile.shift')}
            value={value}
            disabled={readOnly}
            onChange={(event) => {
              setValue(event.target.value);
              setOvertime('0');
            }}
          >
            {availableShifts.map((shift) => (
              <MenuItem key={shift} value={shift}>
                {shift
                  ? `${t(`schedule.shifts.${shift}`)} [${hoursRangeStrFromShift(
                      shift,
                      schedule.fourDPersonnelNames.includes(
                        editing?.name ?? '',
                      ),
                      schedule.shiftHours[editing?.name ?? '']?.DAY ?? 9,
                    )}]`
                  : t('schedule.mobile.empty')}
              </MenuItem>
            ))}
            {!reserve &&
              Object.values(PtoSubtype).map((subtype) => (
                <MenuItem key={subtype} value={subtype}>
                  {t(`pto_subtype.name.${subtype}`)}
                </MenuItem>
              ))}
          </TextField>
          {!reserve && (
            <TextField
              type='number'
              label={t('schedule.overtime_hours')}
              value={overtime}
              disabled={
                readOnly || !value || !shiftOptions.includes(value as Shift)
              }
              error={!validOvertime}
              inputProps={{ step: 0.5, min: -baseHours }}
              onChange={(event) => setOvertime(event.target.value)}
            />
          )}
          <Stack direction='row' justifyContent='flex-end' spacing={1}>
            <Button onClick={() => setEditing(null)}>
              {t('shared.cancel')}
            </Button>
            {!readOnly && (
              <Button
                variant='contained'
                disabled={!reserve && !validOvertime}
                onClick={apply}
              >
                {t('schedule.mobile.apply')}
              </Button>
            )}
          </Stack>
        </Stack>
      </Drawer>
      <Drawer
        anchor='bottom'
        open={reserve && managingPersonnel && !readOnly}
        onClose={() => setManagingPersonnel(false)}
        PaperProps={{
          sx: {
            borderRadius: '16px 16px 0 0',
            maxHeight: '90dvh',
            pb: 'var(--app-safe-area-bottom)',
          },
        }}
      >
        <Stack spacing={2} sx={{ p: 2 }}>
          <Typography variant='h6'>
            {t('schedule.mobile.manageReserve')}
          </Typography>
          {names.map((name) => (
            <Stack key={name} direction='row' spacing={1} alignItems='center'>
              <TextField
                select
                fullWidth
                size='small'
                label={t('personnel.name')}
                value={name}
                onChange={(event) => changePersonnel(name, event.target.value)}
              >
                {[name, ...availablePersonnel].map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
              <IconButton
                color='error'
                aria-label={`${t('shared.delete')} ${name}`}
                onClick={() => changePersonnel(name, '')}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </Stack>
          ))}
          {!!availablePersonnel.length && (
            <TextField
              select
              fullWidth
              size='small'
              label={t('schedule.mobile.addReserve')}
              value=''
              onChange={(event) => changePersonnel('', event.target.value)}
            >
              {availablePersonnel.map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </TextField>
          )}
          <Button onClick={() => setManagingPersonnel(false)}>
            {t('shared.close')}
          </Button>
        </Stack>
      </Drawer>
    </Stack>
  );
}
